import React from "react";
import { Box, Paper, Typography } from "@mui/material";

// Helper to get brightness for calculating text color if not explicitly provided
const getBrightness = (hexColor) => {
  if (!hexColor || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexColor))
    return 128;

  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  // Calculate brightness using a common formula
  return (r * 299 + g * 587 + b * 114) / 1000;
};

// Get appropriate text color based on background color
const getTextColor = (backgroundColor) => {
  return getBrightness(backgroundColor) > 140 ? "#000000" : "#ffffff";
};

const BrandingPreview = ({ colors }) => {
  // Check if colors has valid values and use defaults or calculate text colors if needed
  const validColors = {
    primary: colors?.primary || "#5e35b1",
    primaryLight: colors?.primaryLight || "#9162e4",
    secondary: colors?.secondary || "#ff6f00",
    secondaryLight: colors?.secondaryLight || "#ffad42",
    // Use provided text colors or calculate them based on background colors
    primaryText:
      colors?.primaryText || getTextColor(colors?.primary || "#5e35b1"),
    primaryLightText:
      colors?.primaryLightText ||
      getTextColor(colors?.primaryLight || "#9162e4"),
    secondaryText:
      colors?.secondaryText || getTextColor(colors?.secondary || "#ff6f00"),
    secondaryLightText:
      colors?.secondaryLightText ||
      getTextColor(colors?.secondaryLight || "#ffad42"),
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Live Preview
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 4,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* Left column: Primary Workout Card */}
        <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "48%" } }}>
          <Paper elevation={1} sx={{ overflow: "hidden", borderRadius: 1 }}>
            <Box
              sx={{
                bgcolor: validColors.primary,
                color: validColors.primaryText,
                p: 2,
                fontWeight: "500",
              }}
            >
              Day 1: Full Body Workout
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Focus on compound movements
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: validColors.primary,
                  fontWeight: "bold",
                  mb: 0.5,
                }}
              >
                Barbell Squat
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                3 sets • 8-10 reps • 60s rest
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* Right column: Secondary Workout Card */}
        <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "48%" } }}>
          <Paper elevation={1} sx={{ overflow: "hidden", borderRadius: 1 }}>
            <Box
              sx={{
                bgcolor: validColors.secondary,
                color: validColors.secondaryText,
                p: 2,
                fontWeight: "500",
              }}
            >
              Day 2: Upper Body Focus
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Target chest, back and arms
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: validColors.secondary,
                  fontWeight: "bold",
                  mb: 0.5,
                }}
              >
                Dumbbell Press
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                4 sets • 10-12 reps • 45s rest
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Color Swatches */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <ColorSwatch
          label="Primary"
          color={validColors.primary}
          textColor={validColors.primaryText}
        />
        <ColorSwatch
          label="Primary Light"
          color={validColors.primaryLight}
          textColor={validColors.primaryLightText}
        />
        <ColorSwatch
          label="Secondary"
          color={validColors.secondary}
          textColor={validColors.secondaryText}
        />
        <ColorSwatch
          label="Secondary Light"
          color={validColors.secondaryLight}
          textColor={validColors.secondaryLightText}
        />
      </Box>
    </Box>
  );
};

// Color Swatch Component
const ColorSwatch = ({ label, color, textColor }) => {
  // Show only the hex code without the # for cleaner display
  const hexDisplay = color ? color.replace("#", "") : "";

  return (
    <Box sx={{ textAlign: "center", width: 80 }}>
      <Box
        sx={{
          width: 80,
          height: 80,
          bgcolor: color || "#ccc",
          borderRadius: 1,
          border: "1px solid rgba(0,0,0,0.1)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 1,
          color:
            textColor || (getBrightness(color) > 140 ? "#000000" : "#ffffff"),
          fontSize: "0.75rem",
        }}
      >
        #{hexDisplay}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: "500" }}>
        {label}
      </Typography>
    </Box>
  );
};

export default BrandingPreview;
